import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { LanguageContext } from "../../contexts/LanguageProvider";
import { API_ENDPOINTS } from "../../configs/apiConfig";
import { IoIosClose } from "react-icons/io";
import translations from "./translations";
import { useAlertContext } from '../../contexts/AlertContext';
import BrandSelect from "../common/BrandSelect";

function LinkProductModal({ isOpen, onClose, productId, productName }) {
  const { language } = useContext(LanguageContext);
  const [books, setBooks] = useState([]);
  const [selectedBookId, setSelectedBookId] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [chapterProducts, setChapterProducts] = useState([]);
  const [productChapters, setProductChapters] = useState([]);
  const [loading, setLoading] = useState(false);

  const { showSuccess, showError } = useAlertContext();

  const t = translations[language] || translations.pt;

  // Carregar livros e capítulos vinculados
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      const auth = localStorage.getItem("auth");
      const fetchBooksAndChapters = async () => {
        try {
          const response = await axios.get(API_ENDPOINTS.BOOKS_REFERENCES, {
            headers: { Authorization: `Basic ${auth}` },
          });
          setBooks(response.data);
          setLoading(false);
        } catch (error) {
          console.error("Erro ao carregar referências de livros:", error);
          setLoading(false);
        }
      };

      const fetchProductChapters = async () => {
        try {
          const response = await axios.get(
            API_ENDPOINTS.PRODUCT_CHAPTERS(productId),
            {
              headers: { Authorization: `Basic ${auth}` },
            }
          );
          setProductChapters(response.data);
        } catch (error) {
          console.error("Erro ao carregar capítulos vinculados:", error);
        }
      };

      fetchBooksAndChapters();
      fetchProductChapters();
    }
  }, [isOpen, productId]);

  // Atualizar produtos vinculados quando um capítulo é selecionado
  useEffect(() => {
    if (selectedChapterId) {
      const fetchChapterProducts = async () => {
        try {
          const auth = localStorage.getItem("auth");
          const response = await axios.get(
            API_ENDPOINTS.CHAPTER_PRODUCTS(selectedChapterId),
            {
              headers: { Authorization: `Basic ${auth}` },
            }
          );
          setChapterProducts(response.data);
        } catch (error) {
          console.error("Erro ao carregar produtos vinculados:", error);
        }
      };
      fetchChapterProducts();
    } else {
      setChapterProducts([]);
    }
  }, [selectedChapterId]);

  // Função para desvincular o produto do capítulo
  const handleUnattach = async (chapterId) => {
    try {
      setLoading(true);
      const auth = localStorage.getItem("auth");
      await axios.delete(
        API_ENDPOINTS.PRODUCT_UNATTACH_CHAPTER(productId, chapterId),
        {
          headers: { Authorization: `Basic ${auth}` },
        }
      );

      // Atualizar a lista de capítulos vinculados
      setProductChapters(
        productChapters.filter((link) => link.id !== chapterId)
      );
      showSuccess(t.successUnlink);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao desvincular:", error);
      showError(t.errorUnlink);
      setLoading(false);
    }
  };

  // Função para obter os capítulos do livro selecionado
  const getChaptersForSelectedBook = () => {
    if (!selectedBookId) return [];
    const selectedBook = books.find(
      (book) => book.id === parseInt(selectedBookId)
    );
    return selectedBook ? selectedBook.chapters : [];
  };

  // Função para vincular o produto ao capítulo
  const handleLink = async () => {
    if (!selectedBookId || !selectedChapterId) {
      showError(t.errorLink);
      return;
    }

    try {
      setLoading(true);
      const auth = localStorage.getItem("auth");
      await axios.put(
        API_ENDPOINTS.PRODUCT_ATTACH_CHAPTER(productId, selectedChapterId),
        {},
        {
          headers: { Authorization: `Basic ${auth}` },
        }
      );

      showSuccess(t.successLink);

      // Atualizar capítulos vinculados
      const selectedBook = books.find(
        (book) => book.id === parseInt(selectedBookId)
      );
      const selectedChapter = selectedBook?.chapters.find(
        (ch) => ch.id === parseInt(selectedChapterId)
      );

      if (selectedBook && selectedChapter) {
        const alreadyLinked = productChapters.some(
          (link) => link.id === parseInt(selectedChapterId)
        );

        if (!alreadyLinked) {
          setProductChapters([
            ...productChapters,
            {
              bookId: parseInt(selectedBookId),
              bookTitle: selectedBook.title,
              id: parseInt(selectedChapterId),
              title: selectedChapter.title,
            },
          ]);
        }
      }

      // Atualizar a lista de produtos vinculados ao capítulo
      const response = await axios.get(
        API_ENDPOINTS.CHAPTER_PRODUCTS(selectedChapterId),
        {
          headers: { Authorization: `Basic ${auth}` },
        }
      );
      setChapterProducts(response.data);

      setLoading(false);

      // Limpar seleções após sucesso
      setTimeout(() => {
        setSelectedBookId("");
        setSelectedChapterId("");
      }, 1500);
    } catch (error) {
      console.error("Erro ao vincular:", error);
      showError(t.errorLink);
      setLoading(false);
    }
  };

  // Função para fechar o modal
  const handleClose = () => {
    setSelectedBookId("");
    setSelectedChapterId("");
    setChapterProducts([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t.linkTo}{productName}{t.toChapter}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
            ></button>
          </div>
          <div className="modal-body">
            {loading && <div className="alert alert-info">{t.loading}</div>}

            <div className="mb-3">
              <label className="form-label">{t.selectBook}</label>
              <BrandSelect
                className="w-100"
                value={selectedBookId}
                onChange={(v) => { setSelectedBookId(v); setSelectedChapterId(""); }}
                disabled={loading}
                placeholder={t.selectBookPrompt}
                options={[
                  { value: "", label: t.selectBookPrompt },
                  ...books.map((book) => ({ value: book.id, label: book.title })),
                ]}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">{t.selectChapter}</label>
              <BrandSelect
                className="w-100"
                value={selectedChapterId}
                onChange={(v) => setSelectedChapterId(v)}
                disabled={!selectedBookId || loading}
                placeholder={t.selectChapterPrompt}
                options={[
                  { value: "", label: t.selectChapterPrompt },
                  ...getChaptersForSelectedBook().map((chapter) => ({ value: chapter.id, label: chapter.title })),
                ]}
              />
            </div>

            <div className="mt-4">
              <h6>{t.chapterProducts}</h6>
              {chapterProducts.length > 0 ? (
                <ul className="list-group">
                  {chapterProducts.map((product) => (
                    <li key={product.id} className="list-group-item">
                      {product.name}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t.noLinkedProducts}</p>
              )}
            </div>

            <div className="mt-4">
              <h6>{t.productChapters}</h6>
              {productChapters.length > 0 ? (
                <ul className="list-group">
                  {productChapters.map((link) => (
                    <li
                      key={link.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {link.title}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleUnattach(link.id)}
                        disabled={loading}
                        title={t.unlinkButton}
                        style={{
                          width: "25px",
                          height: "25px",
                          padding: "0",
                          margin: "0",
                          color: "#6c757d",
                          borderRadius: "50%",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IoIosClose size={25} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t.noLinkedChapters}</p>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={loading}
            >
              {t.cancelButton}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleLink}
              disabled={!selectedBookId || !selectedChapterId || loading}
            >
              {t.linkButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LinkProductModal;
